import { Show, createSignal } from "solid-js";

import { ServerMember, User } from "stoat.js";
import { css } from "styled-system/css";
import { styled } from "styled-system/jsx";

import { useLingui } from "@lingui/solid/macro";
import { OverflowingText, Tooltip } from "@revolt/ui";
import { Avatar, Ripple, UserStatus, typography } from "../../design";
import { Row } from "../../layout";

export function ProfileBanner(props: {
  user: User;
  member?: ServerMember;
  bannerUrl?: string;
  onClick?: (e: MouseEvent) => void;
  onClickAvatar?: (e: MouseEvent) => void;
  width: 2 | 3;
}) {
  const { t } = useLingui();

  const [isCopied, setIsCopied] = createSignal(false);

  function copyUsername() {
    navigator.clipboard.writeText(
      `${props.user.username}#${props.user.discriminator}`,
    );
  }

  function onUsernameClick(e: MouseEvent) {
    e.stopPropagation();
    copyUsername();
    setIsCopied(true);

    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  }

  const pronouns = () => props.member?.pronouns ?? props.user.pronouns;

  // Majalis: show a separate display name only when it differs from the username
  const hasName = () =>
    (props.member?.displayName ?? props.user.displayName) !==
    props.user.username;

  return (
    <Banner
      style={{
        "background-image": `linear-gradient(rgba(0, 0, 0, 0.2),rgba(0, 0, 0, 0.7)), url('${props.bannerUrl}')`,
      }}
      isLink={typeof props.onClick !== "undefined"}
      onClick={props.onClick}
      width={props.width}
    >
      <Show when={typeof props.onClick !== "undefined"}>
        <Ripple />
      </Show>

      <Row align gap="lg" style={{ "align-items": "flex-end" }}>
        <Avatar
          src={props.user.animatedAvatarURL}
          size={props.width === 3 ? 112 : 96}
          holepunch="bottom-right"
          onClick={props.onClickAvatar}
          interactive={props.user.avatar && !!props.onClickAvatar}
          overlay={<UserStatus.Graphic status={props.user.presence} />}
        />
        <UserDetails>
          <Show when={hasName()}>
            <span
              class={css({
                fontWeight: 700,
                fontSize: "1.3rem",
                lineHeight: "1.5rem",
              })}
            >
              {props.member?.displayName ?? props.user.displayName}
            </span>
          </Show>
          <Row>
            <UsernameContainer>
              <Tooltip
                content={isCopied() ? t`Copied!` : t`Click to copy username`}
                placement="top"
              >
                <Username big={!hasName()} onClick={onUsernameClick}>
                  {props.user.username}
                  <LowEmphasis class="mj-disc">#{props.user.discriminator}</LowEmphasis>
                </Username>
              </Tooltip>
            </UsernameContainer>
            <Pronouns>
              <OverflowingText>
                <LowEmphasis>{pronouns() ?? ""}</LowEmphasis>
              </OverflowingText>
            </Pronouns>
          </Row>
        </UserDetails>
      </Row>
    </Banner>
  );
}

const Banner = styled("div", {
  base: {
    // for <Ripple />:
    position: "relative",

    userSelect: "none",

    height: "180px",
    padding: "var(--gap-lg)",

    display: "flex",
    flexDirection: "column",
    justifyContent: "end",

    backgroundSize: "cover",
    backgroundPosition: "center",

    borderRadius: "var(--borderRadius-xl)",

    color: "white",
  },
  variants: {
    width: {
      3: {
        gridColumn: "1 / 4",
        height: "148px",
      },
      2: {
        gridColumn: "1 / 3",
      },
    },
    isLink: {
      true: {
        cursor: "pointer",
      },
    },
  },
});

const UserDetails = styled("div", {
  base: {
    ...typography.raw(),

    flexGrow: 1,
    paddingBottom: "6px",
    display: "flex",
    lineHeight: "1rem",
    gap: "var(--gap-xs)",
    flexDirection: "column",
  },
});

const UsernameContainer = styled("div", {
  base: {
    flexShrink: 0,
  },
});

const Username = styled("span", {
  base: {
    cursor: "pointer",
    opacity: 0.9,
    // Majalis: hide the #1234 part until hover; click still copies all of it
    "& .mj-disc": { opacity: 0, transition: "opacity 0.2s ease" },
    _hover: {
      textDecoration: "underline",
      "& .mj-disc": { opacity: 0.8 },
    },
  },
  variants: {
    big: {
      true: {
        opacity: 1,
        fontWeight: 700,
        fontSize: "1.3rem",
        lineHeight: "1.5rem",
      },
    },
  },
});

const LowEmphasis = styled("span", {
  base: {
    fontWeight: 200,
  },
});

const Pronouns = styled("div", {
  base: {
    ...typography.raw(),
    minWidth: 0,
    flexGrow: 1,
    lineHeight: "1rem",
    gap: "var(--gap-xs)",
    textAlign: "right",
  },
});
